/**
 * Dual-channel analytics utility.
 *
 * Wraps Google Analytics 4 (via react-ga4) and Microsoft Clarity in a single
 * API so every call site only needs to think in terms of "identify this user"
 * or "track this event" — each call fans out to both tools.
 *
 * Call sites should not import react-ga4 or src/utils/clarity.js directly for
 * new instrumentation. Use this module instead.
 */
import ReactGA from 'react-ga4';

import {
    identifyUser as clarityIdentifyUser,
    setCustomTags as claritySetCustomTags,
    clearUserIdentification as clarityClearUserIdentification,
    beaconEvent as clarityBeaconEvent,
} from './clarity';
import {
    syncOpsLoggerContextFromAnalytics,
    clearOpsLoggerContextOnLogout,
} from 'src/logger/session';

/**
 * Send a `set('user_properties', …)` call to GA4. Safe to call before GA4 has
 * been initialized — react-ga4 queues calls until initialize() runs.
 *
 * @param {Object} properties - Key/value user properties. Values are coerced
 *   to strings because GA4 user properties are string-typed.
 */
const setGaUserProperties = (properties = {}) => {
    try {
        const stringified = Object.fromEntries(
            Object.entries(properties).filter(([, value]) =>
                value !== undefined && value !== null && value !== ''
            ).map(([key, value]) => [key, String(value)])
        );

        if (Object.keys(stringified).length === 0) return;

        ReactGA.gtag('set', 'user_properties', stringified);
    } catch (error) {
        console.error('Error setting GA4 user_properties:', error);
    }
};

/**
 * Identify the current user across all analytics tools.
 *
 * Clarity — sets the primary identifier (scoped to the user).
 * GA4     — writes user_properties so downstream events are segmentable.
 * Logger  — mirrors non-PII ops fields (agency_id, user_role, …) onto RUM session.
 *
 * @param {string} userId - Primary identifier (email / user_id).
 * @param {Object} properties - Attributes describing the user (role, agency,
 *   is-lyt-employee, etc.). These become both Clarity tags and GA4 user
 *   properties, so they must be non-PII.
 */
export const identifyUser = (userId, properties = {}) => {
    if (!userId) return;

    clarityIdentifyUser(userId, properties);
    claritySetCustomTags(properties);
    setGaUserProperties(properties);
    syncOpsLoggerContextFromAnalytics(properties);
};

/**
 * Update user properties without re-identifying (e.g. mid-session agency
 * switch). Fans out to Clarity custom tags and GA4 user_properties.
 *
 * @param {Object} properties - Properties to update.
 */
export const updateUserProperties = (properties = {}) => {
    if (!properties || Object.keys(properties).length === 0) return;

    claritySetCustomTags(properties);
    setGaUserProperties(properties);
    syncOpsLoggerContextFromAnalytics(properties);
};

/**
 * Clear user identification on logout across analytics and ops telemetry.
 * GA4 user_properties are overwritten on the next identifyUser() call so no
 * explicit GA4 clear is required.
 */
export const clearUserIdentification = () => {
    clarityClearUserIdentification();
    clearOpsLoggerContextOnLogout();
};

/**
 * Track an interaction event across both tools.
 *
 * @param {string} eventName - Human-readable event name (from
 *   ANALYTIC_EVENTS in src/constants/analytic-event-constants.js).
 * @param {Object} eventData - Optional event payload.
 */
export const trackEvent = (eventName, eventData = {}) => {
    if (!eventName) return;

    try {
        ReactGA.gtag('event', eventName, eventData);
    } catch (error) {
        console.error('Error sending GA4 event:', error);
    }

    clarityBeaconEvent(eventName, eventData);
};
